import axios from 'axios';
import { BaseService } from './base.service';

export interface Thread {
  id: string;
  object: string;
  created_at: number;
  metadata: Record<string, any>;
}

export interface Run {
  id: string;
  object: string;
  created_at: number;
  thread_id: string;
  status: 'queued' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  completion_tokens: number;
  prompt_tokens: number;
  metadata: Record<string, any>;
}

export interface Message {
  id: string;
  object: string;
  created_at: number;
  thread_id: string;
  role: string;
  content: Array<{
    type: string;
    text: { value: string; annotations: any[] };
  }>;
}

class OpenAIService extends BaseService<Thread, any, any> {
  private readonly ASSISTANT_ID = import.meta.env.VITE_OPENAI_ASSISTANT_ID;
  protected openAIInstance;

  constructor() {
    super('/openai'); // Base URL for potential future API endpoints
    this.openAIInstance = axios.create({
      baseURL: 'https://api.openai.com/v1',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_OPENAI_API_KEY}`,
        'Content-Type': 'application/json',
        'OpenAI-Beta': 'assistants=v2'
      }
    });
  }

  async createThread(): Promise<Thread> {
    const response = await this.openAIInstance.post('/threads');
    return response.data;
  }

  async addMessage(threadId: string, content: string): Promise<Message> {
    const response = await this.openAIInstance.post(`/threads/${threadId}/messages`, {
      role: 'user',
      content
    });
    return response.data;
  }

  async createRun(threadId: string): Promise<Run> {
    const response = await this.openAIInstance.post(`/threads/${threadId}/runs`, {
      assistant_id: this.ASSISTANT_ID
    });
    return response.data;
  }

  async getRun(threadId: string, runId: string): Promise<Run> {
    const response = await this.openAIInstance.get(`/threads/${threadId}/runs/${runId}`);
    return response.data;
  }

  async getMessages(threadId: string): Promise<{ data: Message[] }> {
    const response = await this.openAIInstance.get(`/threads/${threadId}/messages`);
    return response.data;
  }
}

export const openAIService = new OpenAIService(); 